import { type IntlShape } from 'react-intl'

import { channelDeletionAttribution, type ChannelMessage, type ChannelMessageDeletion } from '@onr/schema'

import { type ChannelMessageItem } from '@/components/templates/ChannelView'
import { type Team } from '@/state/teams/types'

import { teamCarType } from '../dashboard/data/teamCar'

import { channelMessageTeamStatus } from './teamStatus'

export interface ToChannelMessageItemsOptions {
  messages: ReadonlyArray<ChannelMessage & { _id: string }>
  teams: ReadonlyArray<Pick<Team, '_id' | 'vehicle'>>
  intl: IntlShape
  regionName: string
  deletions?: ReadonlyArray<ChannelMessageDeletion & { _id: string }>
}

export const toChannelMessageItems = ({
  messages,
  teams,
  intl,
  regionName,
  deletions,
}: ToChannelMessageItemsOptions): ChannelMessageItem[] => {
  const traces = new Map((deletions ?? []).map((deletion) => [deletion.messageId, deletion]))
  return messages.map((message) => {
    const team = message.teamId ? teams.find((t) => t._id === message.teamId) : undefined
    const trace = traces.get(message._id)
    return {
      _id: message._id,
      sender: message.sender,
      senderLabel:
        message.sender === 'admin'
          ? intl.formatMessage(
              { id: 'channel.sender.central' },
              {
                region: message.senderSysAdmin
                  ? intl.formatMessage({ id: 'channel.sender.nationalOffice' })
                  : regionName,
                name: message.senderName,
              }
            )
          : intl.formatMessage(
              { id: 'channel.sender.team' },
              { teamNumber: message.teamNumber ?? '?', name: message.senderName }
            ),
      createdAt: message.createdAt,
      text: message.text,
      firebaseStoragePath: message.firebaseStoragePath,
      imageWidth: message.imageProperties?.width,
      imageHeight: message.imageProperties?.height,
      audioStoragePath: message.audioStoragePath,
      audioDurationMs: message.audioDurationMs,
      car:
        message.sender === 'volunteer'
          ? {
              state: channelMessageTeamStatus(message.teamStatus),
              carNumber: String(message.teamNumber ?? '?'),
              carType: team ? teamCarType(team.vehicle) : ('automatic' as const),
            }
          : undefined,
      deleted: !!message.deletedAt,
      deletion: trace && {
        text: trace.text,
        firebaseStoragePath: trace.firebaseStoragePath,
        imageWidth: trace.imageProperties?.width,
        imageHeight: trace.imageProperties?.height,
        audioStoragePath: trace.audioStoragePath,
        audioDurationMs: trace.audioDurationMs,
        // The central comes from the trace, not from the page: the consultation view reads its own
        // region name off the link's query string, which anyone can edit.
        deletedByLabel: deletedByLabel(intl, trace),
        deletedAt: trace.deletedAt,
      },
    }
  })
}

const deletedByLabel = (intl: IntlShape, trace: ChannelMessageDeletion) => {
  const attribution = channelDeletionAttribution(trace)
  const central = (region: string) =>
    intl.formatMessage({ id: 'channel.sender.central' }, { region, name: attribution.name })
  switch (attribution.kind) {
    case 'nationalOffice':
      return central(intl.formatMessage({ id: 'channel.sender.nationalOffice' }))
    case 'central':
      return central(attribution.regionName)
    default:
      return attribution.name
  }
}
